import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PageHeader from '../components/PageHeader';
import {
  Users, Plus, Trash2, RefreshCw, Edit3, Shield, Bell,
  BellOff, Eye, EyeOff, Copy, Check, AlertTriangle,
  Heart, Stethoscope, UserCheck, ChevronDown, ChevronUp,
  Mail, Link2
} from 'lucide-react';

const API = import.meta.env.VITE_API_URL;

const ROLE_LABELS: Record<string, { label: string; icon: any; color: string }> = {
  FAMILY_MEMBER: { label: 'Membru Familie', icon: Heart, color: 'text-pink-600 bg-pink-50' },
  CAREGIVER: { label: 'Îngrijitor', icon: UserCheck, color: 'text-green-600 bg-green-50' },
  DOCTOR: { label: 'Medic', icon: Stethoscope, color: 'text-blue-600 bg-blue-50' },
  SUPPORT_PERSON: { label: 'Persoană de Suport', icon: Users, color: 'text-purple-600 bg-purple-50' },
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  PENDING: { label: 'Invitație trimisă', color: 'text-yellow-600 bg-yellow-50 border-yellow-200' },
  ACTIVE: { label: 'Activ', color: 'text-green-600 bg-green-50 border-green-200' },
  DECLINED: { label: 'Refuzat', color: 'text-red-600 bg-red-50 border-red-200' },
};

const PERMISSION_LABELS = [
  { key: 'canViewNutrition', label: 'Alimentație & Calorii', icon: '🍽️' },
  { key: 'canViewMedication', label: 'Medicamente', icon: '💊' },
  { key: 'canViewSymptoms', label: 'Simptome & Durere', icon: '🩺' },
  { key: 'canViewSleep', label: 'Somn', icon: '😴' },
  { key: 'canViewVitals', label: 'Vitale (Puls, Tensiune)', icon: '❤️' },
  { key: 'canViewMood', label: 'Stare Emoțională', icon: '😊' },
];

interface Member {
  id: string;
  nickname: string;
  inviteEmail: string;
  role: string;
  status: string;
  permissions: Record<string, boolean>;
  alertsEnabled: boolean;
  alertEmail: string;
  invitedAt: string;
  acceptedAt: string | null;
  lastViewedAt: string | null;
  viewCount: number;
  monitorUrl: string | null;
}

interface InviteForm {
  inviteEmail: string;
  nickname: string;
  role: string;
  permissions: Record<string, boolean>;
  alertsEnabled: boolean;
  alertEmail: string;
}

export default function CareCirclePage() {
  const navigate = useNavigate();
  const [members, setMembers] = useState<Member[]>([]);
  const [limit, setLimit] = useState({ canAdd: false, current: 0, limit: 0, tier: 'FREE' });
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [expandedMember, setExpandedMember] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const defaultForm: InviteForm = {
    inviteEmail: '',
    nickname: '',
    role: 'FAMILY_MEMBER',
    permissions: {
      canViewNutrition: true,
      canViewMedication: true,
      canViewSymptoms: true,
      canViewSleep: true,
      canViewVitals: true,
      canViewMood: false,
    },
    alertsEnabled: true,
    alertEmail: '',
  };
  const [form, setForm] = useState<InviteForm>(defaultForm);

  const token = localStorage.getItem('accessToken');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [membersRes, limitRes] = await Promise.all([
        axios.get(`${API}/api/care-circle`, { headers }),
        axios.get(`${API}/api/care-circle/limit`, { headers }),
      ]);
      setMembers(membersRes.data.members || []);
      setLimit(limitRes.data);
    } catch (err: any) {
      setError('Eroare la încărcarea datelor');
    } finally {
      setLoading(false);
    }
  }

  async function handleInvite() {
    if (!form.inviteEmail || !form.nickname) {
      setError('Email și numele sunt obligatorii');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/api/care-circle/invite`, {
        inviteEmail: form.inviteEmail,
        nickname: form.nickname,
        role: form.role,
        permissions: form.permissions,
        alertsEnabled: form.alertsEnabled,
        alertEmail: form.alertEmail || form.inviteEmail,
      }, { headers });
      setSuccess('Invitație trimisă cu succes! Un email a fost trimis persoanei.');
      setShowInviteModal(false);
      setForm(defaultForm);
      loadData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Eroare la trimiterea invitației');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUpdatePermissions(memberId: string, data: any) {
    try {
      await axios.put(`${API}/api/care-circle/${memberId}/permissions`, data, { headers });
      setSuccess('Permisiuni actualizate!');
      setEditingMember(null);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Eroare la actualizare');
    }
  }

  async function handleRegenerateLink(memberId: string) {
    if (!confirm('Generezi un link nou? Vechiul link va fi invalidat imediat.')) return;
    try {
      await axios.post(`${API}/api/care-circle/${memberId}/regenerate-link`, {}, { headers });
      setSuccess('Link nou generat! Un email a fost trimis persoanei.');
      loadData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Eroare la regenerare link');
    }
  }

  async function handleRevoke(memberId: string, nickname: string) {
    if (!confirm(`Ești sigur că vrei să revoci accesul lui "${nickname}"? Link-ul lor va fi invalidat imediat.`)) return;
    try {
      await axios.delete(`${API}/api/care-circle/${memberId}`, { headers });
      setSuccess('Accesul a fost revocat.');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Eroare la revocare');
    }
  }

  function copyLink(member: Member) {
    if (!member.monitorUrl) return;
    const fullUrl = `${window.location.origin}${member.monitorUrl}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedId(member.id);
    setTimeout(() => setCopiedId(null), 2000);
  }

  function formatDate(dateStr: string | null) {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ro-RO', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  const tierColors: Record<string, string> = {
    FREE: 'text-gray-600',
    BASIC: 'text-blue-600',
    PREMIUM: 'text-purple-600',
    UNLIMITED: 'text-yellow-600',
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader title="Cercul Meu de Îngrijire" />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Mesaje */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-red-700 text-sm">{error}</p>
            <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">✕</button>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
            <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
            <p className="text-green-700 text-sm">{success}</p>
          </div>
        )}

        {/* Header Info */}
        <div className="bg-gradient-to-br from-blue-600 to-purple-700 rounded-2xl p-6 text-white mb-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-white/20 rounded-xl">
                  <Users className="w-6 h-6" />
                </div>
                <h2 className="text-xl font-bold">Cercul de Îngrijire</h2>
              </div>
              <p className="text-blue-100 text-sm leading-relaxed max-w-md">
                Permite familiei sau medicului tău să monitorizeze indicatorii tăi de sănătate.
                Tu controlezi ce pot vedea și poți revoca accesul oricând.
              </p>
            </div>
            <div className="text-right">
              <div className={`text-xs font-semibold px-3 py-1 rounded-full bg-white/20 ${tierColors[limit.tier] || 'text-white'} text-white`}>
                Plan {limit.tier}
              </div>
              <p className="text-blue-100 text-xs mt-2">
                {limit.current}/{limit.limit === 999 ? '∞' : limit.limit} persoane
              </p>
            </div>
          </div>

          {/* Plan upgrade notice */}
          {limit.tier === 'FREE' && (
            <div className="mt-4 p-3 bg-white/10 rounded-xl border border-white/20">
              <p className="text-sm text-white">
                🔒 Funcția de Cerc de Îngrijire este disponibilă pe planurile plătite.
                <button onClick={() => navigate('/subscriptions')} className="ml-2 underline font-semibold">
                  Fă upgrade →
                </button>
              </p>
            </div>
          )}
        </div>

        {/* Buton Invitare */}
        {limit.tier !== 'FREE' && (
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-500">
              {members.length === 0 ? 'Nicio persoană adăugată încă' : `${members.length} persoană${members.length !== 1 ? 'e' : ''} în cerc`}
            </p>
            <button
              onClick={() => {
                if (!limit.canAdd) {
                  setError(`Ai atins limita de ${limit.limit} persoane pentru planul ${limit.tier}. Fă upgrade pentru mai multe.`);
                  return;
                }
                setShowInviteModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-medium shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Invită o persoană
            </button>
          </div>
        )}

        {/* Lista membrilor */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Se încarcă...</div>
        ) : members.length === 0 && limit.tier !== 'FREE' ? (
          <div className="bg-white rounded-2xl p-12 text-center border-2 border-dashed border-gray-200">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">Nicio persoană adăugată</h3>
            <p className="text-gray-400 text-sm mb-6">
              Invită un membru al familiei sau medicul tău pentru a-ți monitoriza sănătatea.
            </p>
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-medium"
            >
              + Invită prima persoană
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {members.map(member => {
              const roleInfo = ROLE_LABELS[member.role] || ROLE_LABELS.FAMILY_MEMBER;
              const statusInfo = STATUS_LABELS[member.status] || STATUS_LABELS.PENDING;
              const RoleIcon = roleInfo.icon;
              const isExpanded = expandedMember === member.id;

              return (
                <div key={member.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  {/* Header card */}
                  <div className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-xl ${roleInfo.color}`}>
                          <RoleIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 text-lg">{member.nickname}</h3>
                          <p className="text-sm text-gray-500">{member.inviteEmail}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${statusInfo.color}`}>
                              {statusInfo.label}
                            </span>
                            <span className="text-xs text-gray-400">{roleInfo.label}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {/* Copy link */}
                        {member.status === 'ACTIVE' && member.monitorUrl && (
                          <button
                            onClick={() => copyLink(member)}
                            title="Copiază link-ul de monitorizare"
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          >
                            {copiedId === member.id ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                          </button>
                        )}
                        {/* Expand */}
                        <button
                          onClick={() => setExpandedMember(isExpanded ? null : member.id)}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition"
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Stats rapide */}
                    <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                      <span>Invitat: {formatDate(member.invitedAt)}</span>
                      {member.acceptedAt && <span>Acceptat: {formatDate(member.acceptedAt)}</span>}
                      {member.lastViewedAt && <span>Ultima vizualizare: {formatDate(member.lastViewedAt)}</span>}
                      {member.viewCount > 0 && <span>{member.viewCount} vizualizări</span>}
                    </div>
                  </div>

                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 p-5 bg-gray-50">
                      {/* Permisiuni */}
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <Eye className="w-4 h-4" />
                          Ce poate vedea
                        </h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {PERMISSION_LABELS.map(perm => (
                            <div
                              key={perm.key}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${
                                member.permissions[perm.key]
                                  ? 'bg-green-50 text-green-700 border border-green-200'
                                  : 'bg-gray-100 text-gray-400 border border-gray-200'
                              }`}
                            >
                              <span>{perm.icon}</span>
                              <span>{perm.label}</span>
                              {member.permissions[perm.key] ? (
                                <Eye className="w-3 h-3 ml-auto" />
                              ) : (
                                <EyeOff className="w-3 h-3 ml-auto" />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Alerte */}
                      <div className="mb-4 flex items-center gap-2 text-sm">
                        {member.alertsEnabled ? (
                          <><Bell className="w-4 h-4 text-blue-500" /><span className="text-gray-600">Alerte activate pe <strong>{member.alertEmail}</strong></span></>
                        ) : (
                          <><BellOff className="w-4 h-4 text-gray-400" /><span className="text-gray-400">Alerte dezactivate</span></>
                        )}
                      </div>

                      {/* Acțiuni */}
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => setEditingMember(member)}
                          className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition font-medium"
                        >
                          <Edit3 className="w-4 h-4" />
                          Editează permisiuni
                        </button>

                        <button
                          onClick={() => handleRegenerateLink(member.id)}
                          className="flex items-center gap-2 px-3 py-2 text-sm bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100 transition font-medium"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Generează link nou
                        </button>

                        {member.status === 'ACTIVE' && member.monitorUrl && (
                          <button
                            onClick={() => copyLink(member)}
                            className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition font-medium"
                          >
                            <Link2 className="w-4 h-4" />
                            {copiedId === member.id ? 'Copiat!' : 'Copiază link'}
                          </button>
                        )}

                        <button
                          onClick={() => handleRevoke(member.id, member.nickname)}
                          className="flex items-center gap-2 px-3 py-2 text-sm bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition font-medium ml-auto"
                        >
                          <Trash2 className="w-4 h-4" />
                          Revocă accesul
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Info GDPR */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-blue-900 mb-1">Confidențialitate & Control</h4>
              <p className="text-xs text-blue-700 leading-relaxed">
                Tu controlezi complet ce date sunt vizibile. Poți revoca accesul oricând cu un click - link-ul devine invalid imediat.
                Persoanele de suport nu pot modifica datele tale, doar le pot vizualiza conform permisiunilor setate de tine.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Modal Invitație */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 my-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Invită o persoană de suport</h3>
              <button onClick={() => { setShowInviteModal(false); setForm(defaultForm); setError(''); }}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none">✕</button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
            )}

            <div className="space-y-4">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Mail className="w-4 h-4 inline mr-1" />Email *
                </label>
                <input
                  type="email"
                  value={form.inviteEmail}
                  onChange={e => setForm({ ...form, inviteEmail: e.target.value })}
                  placeholder="mama@email.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Nickname */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cum îi spui? *</label>
                <input
                  type="text"
                  value={form.nickname}
                  onChange={e => setForm({ ...form, nickname: e.target.value })}
                  placeholder="Mama, Dr. Popescu, Soț..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Rol */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rol</label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(ROLE_LABELS).map(([key, info]) => {
                    const Icon = info.icon;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setForm({ ...form, role: key })}
                        className={`flex items-center gap-2 p-3 rounded-xl border-2 transition text-left ${
                          form.role === key
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Icon className={`w-4 h-4 ${form.role === key ? 'text-blue-600' : 'text-gray-400'}`} />
                        <span className={`text-sm font-medium ${form.role === key ? 'text-blue-700' : 'text-gray-600'}`}>
                          {info.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Permisiuni */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Eye className="w-4 h-4 inline mr-1" />Ce poate vedea
                </label>
                <div className="space-y-2">
                  {PERMISSION_LABELS.map(perm => (
                    <label key={perm.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition">
                      <span className="text-sm text-gray-700">{perm.icon} {perm.label}</span>
                      <input
                        type="checkbox"
                        checked={form.permissions[perm.key] ?? false}
                        onChange={e => setForm({
                          ...form,
                          permissions: { ...form.permissions, [perm.key]: e.target.checked }
                        })}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                    </label>
                  ))}
                </div>
              </div>

              {/* Alerte */}
              <div>
                <label className="flex items-center justify-between p-3 bg-blue-50 rounded-xl cursor-pointer">
                  <div>
                    <p className="text-sm font-medium text-gray-700">🔔 Alerte automate</p>
                    <p className="text-xs text-gray-500">Trimite email când apar abateri importante</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={form.alertsEnabled}
                    onChange={e => setForm({ ...form, alertsEnabled: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleInvite}
                disabled={submitting}
                className="flex-1 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-semibold disabled:opacity-50"
              >
                {submitting ? 'Se trimite...' : '📧 Trimite Invitația'}
              </button>
              <button
                onClick={() => { setShowInviteModal(false); setForm(defaultForm); setError(''); }}
                className="px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition"
              >
                Anulează
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editare Permisiuni */}
      {editingMember && (
        <EditPermissionsModal
          member={editingMember}
          onSave={(data) => handleUpdatePermissions(editingMember.id, data)}
          onClose={() => setEditingMember(null)}
        />
      )}
    </div>
  );
}

function EditPermissionsModal({ member, onSave, onClose }: {
  member: Member;
  onSave: (data: any) => void;
  onClose: () => void;
}) {
  const [permissions, setPermissions] = useState({ ...member.permissions });
  const [nickname, setNickname] = useState(member.nickname);
  const [alertsEnabled, setAlertsEnabled] = useState(member.alertsEnabled);
  const [alertEmail, setAlertEmail] = useState(member.alertEmail || member.inviteEmail);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 my-4">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">Editează permisiunile</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">✕</button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nume afișat</label>
            <input
              type="text"
              value={nickname}
              onChange={e => setNickname(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Ce poate vedea</label>
            <div className="space-y-2">
              {PERMISSION_LABELS.map(perm => (
                <label key={perm.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition">
                  <span className="text-sm text-gray-700">{perm.icon} {perm.label}</span>
                  <input
                    type="checkbox"
                    checked={permissions[perm.key] ?? false}
                    onChange={e => setPermissions({ ...permissions, [perm.key]: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center justify-between p-3 bg-blue-50 rounded-xl cursor-pointer">
            <div>
              <p className="text-sm font-medium text-gray-700">🔔 Alerte automate</p>
            </div>
            <input
              type="checkbox"
              checked={alertsEnabled}
              onChange={e => setAlertsEnabled(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded"
            />
          </label>

          {alertsEnabled && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email pentru alerte</label>
              <input
                type="email"
                value={alertEmail}
                onChange={e => setAlertEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={() => onSave({ nickname, permissions, alertsEnabled, alertEmail })}
            className="flex-1 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-semibold"
          >
            Salvează
          </button>
          <button onClick={onClose} className="px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition">
            Anulează
          </button>
        </div>
      </div>
    </div>
  );
}
